import { graphql, type DocumentType } from '@/shared/graphql/generated'

/**
 * Alle GraphQL-Dokumente des Content-Features (Kapitel, Artikel, Leseansicht, Audio,
 * Lernfortschritt, Fragebogen-Sichtbarkeit) - jedes genau einmal.
 *
 * Operationsnamen sind appweit eindeutig, weil der Codegen sonst abbricht. Einige Namen
 * sind zudem festgenagelt, weil `features/admin/ContentUploader.tsx` sie in `refetchQueries`
 * per Namen anspricht: GetSliderChapters, GetDiscoverChapters, GetMyTitlesChapters,
 * GetChapterWithArticles, GetVorbereitungCourse.
 *
 * Hasura: Das Schema im Codegen kommt mit Admin-Rechten, die Rolle `user` darf aber nicht
 * jede Spalte lesen. Hier steht deshalb nur, was die Dokumente vorher schon abgefragt
 * haben (plus `id`, wo die Zeile auch geschrieben wird und Apollo sie normalisieren soll).
 */

// ---------------------------------------------------------------------------
// Fragmente
// ---------------------------------------------------------------------------

/**
 * Titelbilder liegen ausschließlich an den Artikeln (`articles.image_id`); ein Kapitel
 * zeigt das Bild seines ersten Artikels. Der Alias ist nötig, weil viele Queries
 * `articles` schon in voller Länge selektieren und `limit: 1` das kaputtmachen würde.
 * Bewusst ohne `live`-Filter - Begründung steht in `@/lib/chapterCover`.
 */
export const CHAPTER_COVER_FRAGMENT = graphql(`
  fragment ChapterCoverFields on chapters {
    cover: articles(order_by: { article_position: asc }, limit: 1) {
      image_id
      image_alt
    }
  }
`)

/** Felder, die jede Abschnitts-Selektion braucht, damit die Formen nicht auseinanderlaufen. */
export const ARTICLE_SECTION_FRAGMENT = graphql(`
  fragment ArticleSectionFields on article_sections {
    id
    section_type
    topic
    content_markdown
    images
    audio
    duration
    position
  }
`)

/** Ein Artikel, wie ihn die Kurzkapitel-Leseansicht braucht - Text, Audio, Abschnitte. */
export const SHORT_CHAPTER_ARTICLE_FRAGMENT = graphql(`
  fragment ShortChapterArticleFields on articles {
    slug
    title
    subtitle
    content_markdown
    sources
    audio
    duration
    article_position
    hook
    takeaway
    summary
    sections(order_by: { position: asc }) {
      ...ArticleSectionFields
    }
  }
`)

// ---------------------------------------------------------------------------
// Wiedereinstieg, Fortschritt, Bezahlstatus
// ---------------------------------------------------------------------------

/**
 * Der Wiedereinstiegspunkt: Kapitel, Artikel und Hörposition in `user_profiles`.
 *
 * Früher viermal vorhanden (AudioContext, Artikelübersicht, Leseansicht,
 * Kurzkapitel-Leseansicht) und in zwei Varianten - eine davon setzte
 * `current_audio_position` fest auf 0 und warf beim Öffnen der Übersicht die gemerkte
 * Sekunde weg. Jetzt ein Dokument mit `$audioPosition`; wer vorn beginnen will (neu
 * gewählter Artikel, Kapitelwechsel), übergibt die 0 ausdrücklich.
 */
export const SAVE_PROGRESS = graphql(`
  mutation SaveProgress(
    $userId: uuid!
    $chapterSlug: String
    $articleSlug: String
    $audioPosition: Int
  ) {
    insert_user_profiles_one(
      object: {
        user_id: $userId
        current_chapter_slug: $chapterSlug
        current_article_slug: $articleSlug
        current_audio_position: $audioPosition
      }
      on_conflict: {
        constraint: user_profiles_user_id_key
        update_columns: [current_chapter_slug, current_article_slug, current_audio_position]
      }
    ) {
      id
    }
  }
`)

/** Gespeicherter Wiedereinstiegspunkt - gelesen von Artikelübersicht und Leseansicht. */
export const GET_USER_PROGRESS = graphql(`
  query GetUserProgress($userId: uuid!) {
    user_profiles(where: { user_id: { _eq: $userId } }, limit: 1) {
      id
      current_chapter_slug
      current_article_slug
      current_audio_position
    }
  }
`) /** Der zuletzt gelesene/gehörte Punkt - Ziel des Weiterlesen-Knopfes in "Meine Titel". */
export const GET_LAST_READING_POSITION = graphql(`
  query GetLastReadingPosition($userId: uuid!) {
    user_profiles(where: { user_id: { _eq: $userId } }, limit: 1) {
      id
      current_chapter_slug
      current_article_slug
    }
  }
`)

// ---------------------------------------------------------------------------
// Lernfortschritt
// ---------------------------------------------------------------------------
//
// Artikel liegen als Zeilen in `article_progress` - eine Zeile pro (user, article), das
// Unique-Constraint dedupliziert in der Datenbank statt im Client. Kapitel bleiben in
// `user_profiles.completed_chapters` (jsonb) als denormalisierter Cache; geschrieben
// wird mit _set statt _append, damit die Liste ein Set bleibt. Erläuterung zur
// Zwei-Ebenen-Logik steht in `@/hooks/useProgress`.

export const GET_PROGRESS = graphql(`
  query GetLearningProgress($userId: uuid!) {
    article_progress(where: { user_id: { _eq: $userId } }) {
      id
      article_slug
      chapter_slug
    }
    user_profiles(where: { user_id: { _eq: $userId } }, limit: 1) {
      id
      completed_chapters
    }
  }
`)

export const MARK_ARTICLE = graphql(`
  mutation MarkArticleComplete($userId: uuid!, $articleSlug: String!, $chapterSlug: String!) {
    insert_article_progress_one(
      object: { user_id: $userId, article_slug: $articleSlug, chapter_slug: $chapterSlug }
      on_conflict: { constraint: article_progress_user_id_article_slug_key, update_columns: [] }
    ) {
      id
      article_slug
      chapter_slug
    }
  }
`)

// insert_one mit on_conflict statt update_user_profiles: Wer nie Fortschritt
// gespeichert hat, besitzt noch gar keine Profilzeile - ein update haette dort
// still 0 Zeilen getroffen und die Kapitelmarkierung verschluckt.
export const SET_COMPLETED_CHAPTERS = graphql(`
  mutation SetCompletedChapters($userId: uuid!, $chapters: jsonb!) {
    insert_user_profiles_one(
      object: { user_id: $userId, completed_chapters: $chapters }
      on_conflict: { constraint: user_profiles_user_id_key, update_columns: [completed_chapters] }
    ) {
      id
      completed_chapters
    }
  }
`)

// Rueckt den Wiedereinstiegspunkt ("Mein Kurs") auf das naechste Kapitel vor. Frueher
// haing das am Beenden-Knopf; jetzt am Kapitelabschluss selbst, damit es auf jedem Weg
// passiert - gelesen, gehoert oder gemischt.
export const ADVANCE_CURRENT_CHAPTER = graphql(`
  mutation AdvanceCurrentChapter($userId: uuid!, $chapterSlug: String!) {
    insert_user_profiles_one(
      object: {
        user_id: $userId
        current_chapter_slug: $chapterSlug
        current_article_slug: null
        current_audio_position: 0
      }
      on_conflict: {
        constraint: user_profiles_user_id_key
        update_columns: [current_chapter_slug, current_article_slug, current_audio_position]
      }
    ) {
      id
    }
  }
`)

// ---------------------------------------------------------------------------
// Kapitellisten
// ---------------------------------------------------------------------------

/** "Mein Kurs" auf der Startseite: die feste Kapitelfolge der Vorbereitung. */
export const GET_VORBEREITUNG_COURSE = graphql(`
  query GetVorbereitungCourse {
    chapters(
      where: { category_slug: { _eq: "vorbereitung" }, articles: { live: { _eq: true } } }
      order_by: { chapter_position: asc_nulls_last, created_at: asc }
    ) {
      id
      slug
      title
      subtitle
      summary
      hook
      ...ChapterCoverFields
      duration
      content_type
      bookmarks {
        id
      }
      articles(order_by: { article_position: asc }, where: { live: { _eq: true } }) {
        slug
        title
        subtitle
        hook
        duration
        sections {
          topic
          duration
        }
      }
    }
    categories(where: { slug: { _eq: "vorbereitung" } }) {
      title
    }
  }
`)

export const GET_DISCOVER_CHAPTERS = graphql(`
  query GetDiscoverChapters {
    chapters(
      order_by: { chapter_position: asc_nulls_last, created_at: asc }
      where: { articles: { live: { _eq: true } } }
    ) {
      id
      slug
      title
      subtitle
      summary
      hook
      ...ChapterCoverFields
      duration
      category_slug
      content_type
      bookmarks {
        id
      }
      articles(where: { live: { _eq: true } }) {
        slug
        title
        subtitle
        hook
        duration
        sections {
          topic
          duration
        }
      }
    }
    categories(order_by: { position: asc_nulls_last, title: asc }) {
      slug
      title
      position
    }
  }
`)

export const GET_MY_TITLES_CHAPTERS = graphql(`
  query GetMyTitlesChapters {
    chapters(
      order_by: { chapter_position: asc_nulls_last, created_at: asc }
      where: { articles: { live: { _eq: true } }, bookmarks: {} }
    ) {
      id
      slug
      title
      subtitle
      summary
      ...ChapterCoverFields
      duration
      content_type
      category_slug
      bookmarks {
        id
        created_at
      }
      articles(where: { live: { _eq: true } }, order_by: { article_position: asc_nulls_last }) {
        id
        slug
        title
        subtitle
        summary
        image_id
        image_alt
        article_position
        duration
        sections {
          topic
          duration
        }
      }
    }
    categories(order_by: { position: asc_nulls_last, title: asc }) {
      slug
      title
    }
  }
`)

export const GET_SLIDER_CHAPTERS = graphql(`
  query GetSliderChapters {
    chapters(
      order_by: { chapter_position: asc_nulls_last, created_at: asc }
      where: { articles: { live: { _eq: true } } }
    ) {
      id
      slug
      title
      subtitle
      hook
      ...ChapterCoverFields
      duration
      content_type
      category_slug
      slider
      bookmarks {
        id
      }
      articles(where: { live: { _eq: true } }) {
        slug
        title
        subtitle
        hook
        duration
        sections {
          topic
          duration
        }
      }
    }
  }
`)

/** Die Reihenfolge für die Kapitelpfeile: Kursfolge mit Kategorie. */
export const GET_ALL_CHAPTER_SLUGS = graphql(`
  query GetAllChapterSlugs {
    chapters(
      order_by: { chapter_position: asc_nulls_last }
      where: { articles: { live: { _eq: true } } }
    ) {
      id
      slug
      category_slug
    }
  }
`)

/** Dieselbe Folge, aber ohne Kategorie - die Leseansicht sucht nur das nächste Kapitel. */
export const GET_CHAPTER_SLUGS_FOR_READING = graphql(`
  query GetChapterSlugsForReading {
    chapters(
      order_by: { chapter_position: asc_nulls_last }
      where: { articles: { live: { _eq: true } } }
    ) {
      id
      slug
    }
  }
`)

// ---------------------------------------------------------------------------
// Kapitel- und Artikelinhalte
// ---------------------------------------------------------------------------

/** Die Artikelübersicht eines Kapitels (Überblick-Tab und Artikelliste). */
export const GET_CHAPTER_WITH_ARTICLES = graphql(`
  query GetChapterWithArticles($slug: String!) {
    chapters(where: { slug: { _eq: $slug } }) {
      id
      slug
      title
      subtitle
      summary
      hook
      takeaway
      duration
      ...ChapterCoverFields
      created_at
      articles(order_by: { article_position: asc }, where: { live: { _eq: true } }) {
        slug
        article_position
        title
        subtitle
        duration
        hook
        takeaway
        summary
        sections {
          id
          topic
          duration
        }
      }
    }
  }
`)

/** Die Leseansicht eines langen Kapitels. */
export const GET_CHAPTER_ARTICLES = graphql(`
  query GetChapterArticles($chapterSlug: String!) {
    chapters(where: { slug: { _eq: $chapterSlug } }) {
      # id und bookmarks tragen den Merken-Knopf auf dem Titelbild; mit id normalisiert
      # Apollo das Kapitel, sodass der Knopf nach dem Umschalten sofort mitgeht.
      id
      title
      summary
      hook
      duration
      bookmarks {
        id
      }
      articles(order_by: { article_position: asc }, where: { live: { _eq: true } }) {
        slug
        title
        subtitle
        content_markdown
        sources
        audio
        article_position
        duration
        hook
        takeaway
        summary
        image_id
        image_alt
        sections(order_by: { position: asc }) {
          ...ArticleSectionFields
        }
      }
    }
  }
`)

/**
 * Die Artikel eines Kurzkapitels - für beide Wege dorthin: die Übersichtsroute
 * (`articleOverview`) und das globale Overlay (`ShortChapterOverlay`). Beide fragten
 * bisher genau dasselbe unter zwei Namen ab; jetzt teilen sie auch den Cache-Eintrag.
 */
export const GET_SHORT_CHAPTER_ARTICLES = graphql(`
  query GetShortChapterArticles($slug: String!) {
    chapters(where: { slug: { _eq: $slug } }) {
      id
      articles(order_by: { article_position: asc }, where: { live: { _eq: true } }) {
        ...ShortChapterArticleFields
      }
    }
  }
`)

/** Kopfdaten des Kurzkapitels im globalen Overlay (Tracking-Seite behält ihren Scrollstand). */
export const GET_SHORT_CHAPTER = graphql(`
  query GetShortChapter($slug: String!) {
    chapters(where: { slug: { _eq: $slug } }) {
      id
      title
      subtitle
      summary
      hook
      takeaway
      duration
      ...ChapterCoverFields
    }
  }
`)

// ---------------------------------------------------------------------------
// Fragebogen und Artikel-Sichtbarkeit
// ---------------------------------------------------------------------------

/**
 * Was der Kurs für diese Nutzerin ausblendet: die Zuordnungstabelle
 * (Antwort -> Artikel) und ihre Antworten. Beides zusammen in einem Dokument, weil
 * `useArticleVisibility` ohne beide Hälften keine Entscheidung treffen kann.
 */
export const GET_ARTICLE_VISIBILITY_DATA = graphql(`
  query GetArticleVisibilityData {
    matching_question_with_chapter {
      answer_key
      article_slugs
      criterion
      exclusive
    }
    preparation(limit: 1) {
      id
      miscarriage
      starchild
      abortionmedical
      abortioncriminal
      adoption
      induce
      sagging
      sharedcare
      betterPre
      personalreason
      rawmilk
      magnesium
      amalgam
      piercing
      herpes
      fungus
      throat
      diabetes
      pcos
      accessory
      contraindication
      sectio
      preemie
      lkg
      trisomie
      drugs
    }
  }
`)

// ---------------------------------------------------------------------------
// Merken (Lesezeichen am Kapitel)
// ---------------------------------------------------------------------------

/**
 * Ein Lesezeichen setzen.
 *
 * `on_conflict` mit leerer `update_columns`: Zweimal merken ist kein Fehler, sondern
 * ein Nichts - der eindeutige Index auf (user_id, chapter_slug) faengt es ab.
 *
 * Die Listen, deren Zusammensetzung davon abhaengt, kommen ueber
 * `CHAPTER_LIST_REFETCHES`; eine neue Zeile in einer anderen Tabelle kann Apollo dem
 * Kapitel im Cache nicht von selbst zuordnen.
 */
export const ADD_CHAPTER_BOOKMARK = graphql(`
  mutation AddChapterBookmark($userId: uuid!, $chapterSlug: String!) {
    insert_chapter_bookmarks_one(
      object: { user_id: $userId, chapter_slug: $chapterSlug }
      on_conflict: { constraint: chapter_bookmarks_user_id_chapter_slug_key, update_columns: [] }
    ) {
      id
      chapter_slug
      created_at
    }
  }
`)

/** Ein Lesezeichen entfernen. */
export const REMOVE_CHAPTER_BOOKMARK = graphql(`
  mutation RemoveChapterBookmark($userId: uuid!, $chapterSlug: String!) {
    delete_chapter_bookmarks(
      where: { user_id: { _eq: $userId }, chapter_slug: { _eq: $chapterSlug } }
    ) {
      returning {
        id
      }
    }
  }
`)

/** Merkt eine ganze Gruppe auf einmal. */
export const ADD_CHAPTER_BOOKMARKS = graphql(`
  mutation AddChapterBookmarks($objects: [chapter_bookmarks_insert_input!]!) {
    insert_chapter_bookmarks(
      objects: $objects
      on_conflict: { constraint: chapter_bookmarks_user_id_chapter_slug_key, update_columns: [] }
    ) {
      returning {
        id
        chapter_slug
      }
    }
  }
`)

/** Hebt das Merken fuer eine ganze Gruppe auf. */
export const REMOVE_CHAPTER_BOOKMARKS = graphql(`
  mutation RemoveChapterBookmarks($userId: uuid!, $chapterSlugs: [String!]!) {
    delete_chapter_bookmarks(
      where: { user_id: { _eq: $userId }, chapter_slug: { _in: $chapterSlugs } }
    ) {
      returning {
        id
      }
    }
  }
`)

/**
 * Die Listen, in denen ein umgelegtes Lesezeichen die Zusammensetzung ändert ("Meine
 * Titel" zeigt nur gemerkte Kapitel). Sie müssen neu geladen werden, weil Apollo eine
 * neue Zeile in `chapter_bookmarks` dem Kapitel im Cache nicht von selbst zuordnet.
 *
 * `GetAllChaptersForPreview` bleibt als Name stehen: Die Query gehört zur CMS-Vorschau
 * (`features/admin/PreparationPreview.tsx`), die noch mit String-Fragmenten arbeitet und
 * deshalb kein importierbares Dokument hat.
 */
export const CHAPTER_LIST_REFETCHES = [
  GET_SLIDER_CHAPTERS,
  GET_DISCOVER_CHAPTERS,
  GET_MY_TITLES_CHAPTERS,
]

// ---------------------------------------------------------------------------
// Typen aus den Dokumenten
// ---------------------------------------------------------------------------

type ChapterArticlesResult = DocumentType<typeof GET_CHAPTER_ARTICLES>
type ArticleFromQuery = ChapterArticlesResult['chapters'][number]['articles'][number]
type SectionFromQuery = ArticleFromQuery['sections'][number]

/**
 * Ein Abschnitt eines Artikels (Text, Bilder oder Video) in der Leseansicht.
 *
 * `section_type` und `images` sind im Schema NOT NULL und kommen deshalb als
 * Pflichtfelder aus dem Codegen. Hier bleiben sie optional, weil die CMS-Vorschau
 * dieselben Karten mit einer eigenen, lockereren Auswahl rendert. Gelesen werden beide
 * ohnehin nur über Helfer mit Rückfall (`isImageSection`, `parseSectionImages`).
 */
export type ReadingArticleSection = Omit<SectionFromQuery, 'section_type' | 'images'> & {
  section_type?: string | null
  images?: unknown
}

/**
 * Ein Artikel in der Leseansicht. Kommt aus `GetChapterArticles`, nur `sections` bleibt
 * optional: Die Kurzkapitel-Queries und die CMS-Vorschau liefern Artikel, deren
 * Abschnitte je nach Query fehlen dürfen.
 */
export type ReadingArticle = Omit<ArticleFromQuery, 'sections'> & {
  sections?: ReadingArticleSection[]
}

/**
 * Bringt ein Kapitel aus einer der Listen-Queries in die Form, die die Kartenliste
 * (`ChapterCardData`) erwartet.
 *
 * Im Schema sind `slug`, `hook` und `content_type` nullable und kommen deshalb als
 * optionale Felder aus dem Codegen, während die Karte sie als vorhanden verlangt. Die
 * Werte bleiben, was sie waren - gefüllt wird nur die Lücke. `duration` übergibt der
 * Aufrufer, weil sie je nach Leseeinstellungen aus den Artikeln neu summiert wird.
 */
export function toCardChapter<
  T extends {
    slug?: string | null
    hook?: string | null
    content_type?: string | null
    bookmarks?: { id: string }[] | null
  },
>(chapter: T, duration: number | null) {
  return {
    ...chapter,
    slug: chapter.slug ?? '',
    hook: chapter.hook ?? null,
    content_type: chapter.content_type ?? null,
    // Die Zeilenrechte liefern nur die eigenen Lesezeichen - eine nichtleere Liste
    // heisst also "von mir gemerkt".
    isBookmarked: (chapter.bookmarks?.length ?? 0) > 0,
    duration,
  }
}
